import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AuthAdaptorService, AuthApiService, AuthProviderMfaEnrollerToken, AuthService, ContactUser } from '@aiwyn/auth';
import { ConfigService } from '@aiwyn/config';
import { Router } from '@angular/router';
import { ROUTE_TOKEN_REQUEST_PASSWORD_RESET } from '../../../routing-constants';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { Logger } from '@aiwyn/shared';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: false,
})
export class AccountSettingsComponent implements OnInit {
  private readonly configService = inject(ConfigService);
  private readonly authService = inject(AuthService);
  private readonly mfaEnrollerService = inject(AuthProviderMfaEnrollerToken)
  private readonly logger = inject(Logger);
  private readonly authApiService = inject(AuthApiService);

  user = this.authService.getUserSignal();

  displayMfaSettings = computed(() =>
    /* If they have it set up, they need to be able to change their settings, regardless of whether the config is set up */
    this.user()?.hasEnrolledInMfa()
    /* They can set it up or are required to */
    || ['OPTIONAL', 'REQUIRED'].includes(this.configService.multiFactorAuthentication)
  );
  processingMfaUpdate = signal(false);

  // Used hide password from view.
  hide = true;
  // Used to disable the ability to change password unless change has been clicked.
  changePasswordDisabled = true;
  // FormGroup for account-settings component.
  accountSettingsForm: UntypedFormGroup;
  // Password field.
  password = "fakepassword";

  constructor(
    public authAdaptor: AuthAdaptorService,
    private router: Router,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    // Create a form group.
    if (this.authAdaptor.user) {
      const user = this.authAdaptor.user;
      this.accountSettingsForm = this.fb.group({
        userDisplayName: user.getDisplayName(),
        userEmailAddress: user instanceof ContactUser ? user.getContact().username : user.getProviderEmail(),
        userPassword: new UntypedFormControl({value: this.password, disabled: this.changePasswordDisabled}),
      });
    }
  }

  /**
   * Kick off a password reset
   */
  changePassword(): void {
    this.router.navigateByUrl(ROUTE_TOKEN_REQUEST_PASSWORD_RESET);
  }

  showMfaEnrollmentDialog(): void {
    this.processingMfaUpdate.set(true);
    this.mfaEnrollerService.resolveEnrollment(this.configService.multiFactorAuthentication === 'REQUIRED').pipe(
      take(1),
      switchMap(result => {
        if (result.signOut) {
          return this.authService.signOut(true);
        }

        if (result.completed) {
          if (result.phone) {
            //Enrollment was updated
            return this.authApiService.updateContactMfaEnrollment(this.user().getProviderEmail(), true, result.phone);
          } else {
            // User un-enrolled
            return this.authApiService.updateContactMfaEnrollment(this.user().getProviderEmail(), false);
          }
        }

        return EMPTY
      }),
      catchError(error => {
        this.logger.warn('AccountSettingsComponent :: showMfaEnrollmentDialog :: Failed to update MFA enrollment', error);
        return EMPTY;
      }),
      finalize(() => this.processingMfaUpdate.set(false))
    ).subscribe();
  }
}
